import jwtDecode from "jwt-decode";
import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import ReactLoading from "react-loading";

// Roles that are exempt from the location requirement
const EXEMPT_ROLES = [
  "Super Admin",
  "Developer",
  "Auction Super Admin",
  "Auction CEO",
  "Bharat Loan Business Head",
];

function needsLocation(user) {
  if (!user) return false;
  // desktop user
  if (user.phone === "9518329252") return false;
  // Special BA user exempt
  if (user.phone === "9175894368") return true;
  const isExempt = user?.assigned_states_designations?.some((item) =>
    item?.designations?.some(({ designation }) =>
      EXEMPT_ROLES.includes(designation),
    ),
  );
  return !isExempt;
}

// 'idle' | 'checking' | 'granted' | 'denied'
function LocationPermissionModal() {
  const [locationState, setLocationState] = useState("idle");
  const [retryFailed, setRetryFailed] = useState(false);

  let user = null;
  try {
    const token = localStorage.getItem("refreshToken");
    if (token) user = jwtDecode(token);
  } catch {}

  const required = needsLocation(user);

  const checkPermission = (isRetry = false) => {
    if (!navigator.geolocation) {
      setLocationState("denied");
      if (isRetry) setRetryFailed(true);
      return;
    }

    setRetryFailed(false);

    // Use Permissions API first — instant, no GPS fix needed
    if (navigator.permissions) {
      navigator.permissions
        .query({ name: "geolocation" })
        .then((result) => {
          if (result.state === "granted") {
            setLocationState("granted");
          } else if (result.state === "denied") {
            setLocationState("denied");
            if (isRetry) setRetryFailed(true);
          } else {
            // 'prompt' — trigger the browser dialog by calling getCurrentPosition
            setLocationState("checking");
            navigator.geolocation.getCurrentPosition(
              () => setLocationState("granted"),
              () => {
                setLocationState("denied");
                if (isRetry) setRetryFailed(true);
              },
              { timeout: 10000 },
            );
          }
        })
        .catch(() => {
          // Permissions API not supported — fall back to getCurrentPosition
          setLocationState("checking");
          navigator.geolocation.getCurrentPosition(
            () => setLocationState("granted"),
            () => {
              setLocationState("denied");
              if (isRetry) setRetryFailed(true);
            },
            { timeout: 10000 },
          );
        });
    } else {
      // No Permissions API — fall back directly
      setLocationState("checking");
      navigator.geolocation.getCurrentPosition(
        () => setLocationState("granted"),
        () => {
          setLocationState("denied");
          if (isRetry) setRetryFailed(true);
        },
        { timeout: 10000 },
      );
    }
  };

  useEffect(() => {
    if (required) checkPermission();
  }, [required]); // eslint-disable-line react-hooks/exhaustive-deps

  // Not required or already granted — render nothing
  if (!required || locationState === "granted" || locationState === "idle")
    return null;

  return (
    <>
      <style>{`.loc-perm-backdrop { z-index: 1060 !important; }`}</style>
      <Modal
        show
        backdrop="static"
        keyboard={false}
        centered
        size="md"
        backdropClassName="loc-perm-backdrop"
        style={{ zIndex: 1070 }}
      >
        <Modal.Header
          style={{
            background: "linear-gradient(135deg, #1a3c5e, #0a58ca)",
            color: "#fff",
            borderBottom: "none",
          }}
        >
          <Modal.Title as="div" style={{ fontSize: "1.5rem", fontWeight: 600 }}>
            <Icon.ShieldLockFill className="me-2" />
            Account Security Check
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ padding: "28px 24px" }}>
          {locationState === "checking" ? (
            <div className="text-center py-2">
              <ReactLoading
                type="bubbles"
                color="#0d6efd"
                height={80}
                width={80}
                className="mx-auto"
              />
              <p className="text-muted mt-2 fw-semibold">
                One moment — verifying your location 🌍
              </p>
              <p className="text-muted" style={{ fontSize: "0.85rem" }}>
                A browser prompt may appear asking for location access — go
                ahead and tap <strong>Allow</strong>. It keeps your account
                secure. 🔐
              </p>
            </div>
          ) : (
            <div className="text-center">
              <div style={{ fontSize: "3.5rem" }} className="mb-3">
                🛡️
              </div>
              <h5 className="fw-bold mb-2" style={{ color: "#1a3c5e" }}>
                We're Looking Out For You
              </h5>
              <p
                className="text-muted mb-3"
                style={{ fontSize: "0.92rem", lineHeight: 1.7 }}
              >
                Your account's security is our top priority. Location
                verification is a{" "}
                <strong>safety shield we've built for you</strong> — it ensures
                that only <em>you</em> can access MeraTractor ERP from your
                device, keeping unauthorised logins at bay. Think of it as your
                personal guardian. 🌟
              </p>

              <div
                className="rounded-3 p-3 text-start mx-auto mb-3"
                style={{
                  background: "#f0f7ff",
                  border: "1px solid #bee3f8",
                  maxWidth: "340px",
                  fontSize: "0.83rem",
                  color: "#1a3c5e",
                }}
              >
                <div className="fw-semibold mb-2">
                  ✨ Enable location in 3 easy steps:
                </div>
                <div className="mb-1">
                  🔒 Click the <strong>lock icon</strong> in your browser's
                  address bar
                </div>
                <div className="mb-1">
                  📍 Set <strong>Location</strong> to <strong>Allow</strong>
                </div>
                <div>
                  🔄 Hit <strong>Retry</strong> below — you're all set!
                </div>
              </div>

              <p
                className="mb-4"
                style={{ fontSize: "0.78rem", color: "#6b7280" }}
              >
                We never share your location with anyone outside MeraTractor.
                Your privacy is as important to us as your security. 🔐
              </p>

              <button
                className="btn btn-primary px-5"
                style={{ borderRadius: "10px", fontWeight: 600 }}
                onClick={() => checkPermission(true)}
              >
                <Icon.ArrowClockwise size="0.9rem" className="me-2" />
                I've Enabled It — Let Me In!
              </button>

              {retryFailed && (
                <div
                  className="d-flex align-items-center justify-content-center gap-1 mt-2"
                  style={{ fontSize: "0.8rem", color: "#dc3545" }}
                >
                  <Icon.ExclamationCircleFill size="0.75rem" />
                  Hmm, location is still not enabled. Please check your browser
                  settings and try again.
                </div>
              )}
            </div>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
}

export default LocationPermissionModal;
